import React, { useState } from 'react';

import BaseList from '@/components/base-list';
import { DropdownButton } from '@/components/dropdown-button/dropdown-button';
import ProgressBar from '@/components/progress-bar/progress-bar';
import { useApi } from '@/hooks/useApi';
import useLayout from '@/hooks/useLayout';
import { isValidDateString } from '@/utility/helper/dateUtils';
import { useSnackbar } from '@rrexpress/core';
import dayjs from 'dayjs';
import { ceil } from 'lodash';

const ImportProgressList: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const { sendBasicRequest } = useApi();
  const { show } = useSnackbar();

  useLayout({ title: 'Import Progress' });

  async function handleDownloadExcel(fileName: string, filePath: string, customFileName: string) {
    setIsLoading(true);
    try {
      const resp = await sendBasicRequest(
        `/files/retrieve?file_name=${fileName}&file_path=${filePath}`,
        'GET',
        {},
        {
          responseType: 'blob',
        },
      );

      const blobUrl = URL.createObjectURL(resp);

      const link = document.createElement('a');
      link.href = blobUrl;

      link.download = customFileName || 'download.xlsx';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
      if (resp) {
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      show('Failed to download file', 'error');
    }
  }

  const column: any[] = React.useMemo(() => [
    {
      id: '1',
      accessorKey: 'import_name',
      header: 'Import Name',
    },
    {
      id: '2',
      accessorKey: 'import_date_time_utc',
      header: 'Tanggal Import',
      cell: ({ getValue }: any) => (
        isValidDateString(getValue()) ? dayjs(getValue()).format('DD-MM-YYYY') : '-'
      ),
    },
    {
      id: '3',
      accessorKey: 'import_file_name',
      header: 'File Name',
    },
    {
      id: '4',
      accessorKey: 'import_file_type',
      header: 'File Type',
    },
    {
      id: '5',
      accessorKey: 'import_file_size',
      header: 'File Size',
      cell: ({ getValue }: any) => (
        <span>{(getValue() / (1024 * 1024)).toFixed(2)} Mb</span>
      ),
    },
    {
      id: '6',
      accessorKey: 'total_records',
      header: 'Total Records',
    },
    {
      id: '7',
      accessorKey: 'total_records_success',
      header: 'Total Records Success',
    },
    {
      id: '8',
      accessorKey: 'total_records_failed',
      header: 'Total Records Failed',
    },
    {
      id: '9',
      accessorKey: 'total_records_in_progress',
      header: 'Progress',
      cell: ({ getValue, row }: any) => {
        const progress = row?.original?.total_records_processed && row?.original?.total_records
          ? ceil((row.original.total_records_processed / row.original.total_records) * 100)
          : 0;
        return (
          <ProgressBar progress={progress} />
        );
      },
    },
    {
      id: '10',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }: any) => (
        <span className="capitalize">{getValue()}</span>
      ),
    },
    {
      id: '11',
      accessorKey: 'status',
      header: 'Actions',
      cell: ({ getValue, row }: any) => (
        <DropdownButton
          size="sm"
          items={[
            {
              label: 'Download Original File',
              onClick: () => handleDownloadExcel(
                row?.original?.import_file_name,
                row?.original?.import_file_path,
                row?.original?.import_file_name,
              ),
              disabled: false,
            },
            {
              label: 'Download Success File',
              onClick: () => handleDownloadExcel(
                row?.original?.report_success_file_name,
                row?.original?.report_success_file_path,
                row?.original?.report_success_file_name,
              ),
              disabled: row?.original?.total_records_success <= 0,
            },
            {
              label: 'Download Failed File',
              onClick: () => handleDownloadExcel(
                row?.original?.report_failed_file_name,
                row?.original?.report_failed_file_path,
                row?.original?.report_failed_file_name,
              ),
              disabled: row?.original?.total_records_failed <= 0,
            },
          ]}
        >
          Download
        </DropdownButton>
      ),
    },
  ], []);

  return (
    <BaseList
      resource="import-summaries"
      title="Import Progress"
      columns={column}
      showSortFilter
    />
  );
};

export default ImportProgressList;
